import EF from 'ef/ef';
import Chaplin from 'chaplin';
import ActivitiesCollection from 'ef/collections/activities_collection';
import ProgressTracker from 'ef/models/progress_tracker';
import _ from 'lodash';
import EnrollmentDataService from 'services/data-handler-services/enrollment-data-service';
// # EF.Models.Module
//
// Module is the top level model under which the activities and pages are
// nested. This model provides an interface for retrieving data about the
// activties and pages.
const Module = Chaplin.Model.extend({
  idAttribute: 'key',
  // initialize function called by backbone when instantiating objects.
  // pass an attributes object with an activities keys and it will
  // instantiate an Activities Collection, available at `activities`
  // This method also instantiates the Progress Tracker for the module.
  //
  initialize: function (attrs) {
    if (EF.groups) {
      const group = EF.groups.find({ id: this.get('groupId') });
      if (group) {
        group.get('modules').add(this);
      }
      this.set('group', group);
    }

    this.currentActivity = null;
    _.bindAll(this, 'attachCompletionEvents', 'setActivityByKey', 'applyModuleStatus');
    this.initActivities(attrs.activities, { module: this });

    this.on('add', this.applyModuleStatus);
    this.on('sync', this.applyModuleStatus);
    this.on('change:page_key', this.applyModuleStatus);
    this.initProgress();
  },

  moduleStatusProgress: function () {
    const pkey = this.get('page_key');
    const akey = this.get('activity_key');
    if (!(pkey && akey)) {
      return {};
    }
    const activity = this.activities.findWhere({ key: akey });
    const page = activity ? activity.pages.findWhere({ key: pkey }) : null;
    return { activity, page };
  },

  // If a module is added with module status attributes, apply them to the
  // module by going through and setting the right pages to completed.
  applyModuleStatus: function () {
    const moduleStatus = this.get('status');
    const { activity, page } = this.moduleStatusProgress();
    if (activity) {
      this.activities.each(a => {
        if (a.get('index') < activity.get('index') || moduleStatus === 'completed') {
          a.completeUpToPage(a.pages.last());
        } else if (a.get('index') === activity.get('index') && page) {
          a.completeUpToPage(page);
        }
      });
    }
    this.set('progressLoaded', true);
    this.set('completed', this.isComplete());
  },

  url: function () {
    const eid = EF.enrollment.get('id');
    return EF.url(`/sdk/enrollments/${eid}/module_statuses/${this.id}.json`);
  },

  toJSON: function () {
    const { page_key, key, activity_key, completed } = this.attributes;
    return { page_key, activity_key, completed, module_key: key };
  },

  setLastCompletedPage: function (page) {
    const progress = this.moduleStatusProgress();
    if (progress.page) {
      if (
        page.get('index') > progress.page.get('index') ||
        page.activity.get('index') > progress.activity.get('index')
      ) {
        this.set('page_key', page.get('key'));
        this.set('activity_key', page.activity.get('key'));
      }
    } else {
      this.set('page_key', page.get('key'));
      this.set('activity_key', page.activity.get('key'));
    }
    this.set('completed', this.isComplete());
  },

  defaults: {
    progressLoaded: false,
    completed: false,
    status: 'incomplete',
  },

  initActivities: function (activities, options) {
    this.activities = new ActivitiesCollection(null, options);
    this.activities.reset(activities);
    this.set('activities', this.activities);
  },

  initProgress: function () {
    const self = this;
    this.progress = new ProgressTracker({ module_key: this.get('key') }, { module: this });
    // this.progress = new ModuleStatusProgress({module: this});
    this.set('progress', this.progress);
    this.on('change:progressLoaded', function () {
      if (self.get('progressLoaded')) {
        EF.trigger('module:progress:set', self);
        self.attachCompletionEvents();
      }
    });
  },

  attachCompletionEvents: function () {
    const self = this;

    this.set('completed', this.isComplete());

    this.on('change:completed', function () {
      if (self.get('completed')) {
        self.set('status', 'complete');
        if (!this.get('skip_activity_progress')) {
          EF.ContentProvider.ModuleService.markComplete(self);
        }

        // Save progress for last skipped pages in the module.
        self.saveLastSkippedModulePage();
      }
    });

    this.activities.on('change:completed', function () {
      self.set('completed', self.isComplete());
    });
  },

  // ### loadProgress
  //
  // load progress is responsible for loading the user's progress for this
  // module and setting it on the page objects in all of the activities.
  loadProgress: function (callback) {
    // Not sure where all this is being called from, but making it a noop
    // in light of the module status upgrades
    if (callback) {
      callback();
    }
  },

  _setProgressOnPage: function (page, pageProg) {
    if (pageProg) {
      page.set(pageProg);
      page.set('pageData', pageProg.data);
    }
  },

  _setProgressOnPages: function (progress) {
    const self = this;
    if (progress) {
      this.activities.each(function (activity) {
        let actProgress = progress[activity.get('key')];
        actProgress = actProgress || {};
        activity.pages.each(function (page) {
          const pageProg = actProgress[page.get('key')];
          self._setProgressOnPage(page, pageProg);
        });
        activity.trigger('progress_loaded');
      });
      // this.trigger('progress_loaded');
    } else {
      this.activities.each(function (a) {
        a.attachCompletionEvents();
      });
      this.attachCompletionEvents();
    }
  },

  //
  //  ### getStagingPage
  //
  //  returns the starting page for this module.
  //  The starting page is determined by the farthest progress + 1.
  //  If there is no activity set it will select the first one.
  //  TODO: Make starting page the farthest progress + 1 for the user
  getStartingPage: function () {
    if (this.currentActivity) {
      return this.currentActivity.getStartingPage();
    } else {
      return false;
    }
  },

  //
  // ### getStartingActivity
  //
  // retrieves the starting activity, which is the first activity that is not
  // completed
  getStartingActivity: function () {
    const firstNotComplete = this.activities.find(activity => !activity.isComplete());

    if (firstNotComplete) {
      this.currentActivity = firstNotComplete;
      return firstNotComplete;
    } else if (this.get('complete') && this.get('redoActivityKey')) {
      this.currentActivity = this.activities.getActivityByKey(this.get('redoActivityKey'));
      return this.currentActivity;
    } else {
      this.currentActivity = null;
      return false;
    }
  },

  getFirstActivity: function () {
    return this.activities.at(0);
  },

  getFirstPage: function () {
    return this.getFirstActivity().pages.at(0);
  },

  setActivity: function (activity) {
    this.currentActivity = activity;
  },

  // sets the current Activity on the module by key.
  setActivityByKey: function (activity_key) {
    this.currentActivity = this.activities.getActivityByKey(activity_key);
    this.currentActivity.currentPage = undefined;
    return this.currentActivity;
  },

  // ### nextPage
  //
  // returns the next page in the activity, but does not set it to the current
  // page on the activity. Useful to seeing what is next for preloading.
  nextPage: function () {
    // if complete and is redo, return next page. if none trigger completed again.
    if (this.currentActivity.isComplete() && this.currentActivity.get('redo')) {
      const p = this.currentActivity.getNextPage();
      if (!p) {
        this.currentActivity.trigger('change:completed');
        return false;
      } else {
        return p;
      }
    }
    //
    // if complete and not redo, we're done
    else if (this.currentActivity.isComplete() && !this.currentActivity.get('redo')) {
      if (!EF.options.autoNextActivity) {
        return false;
      }
      this.getNextActivity();
      if (!this.currentActivity) {
        return false;
      }
      return this.currentActivity.getStartingPage();
    } else {
      return this.currentActivity.getNextPage();
    }
  },

  // sets the activity on the module by finding the first uncompleted one.
  getNextActivity: function () {
    this.currentActivity = this.activities.find(function (activity) {
      return !activity.isComplete();
    });
    return this.currentActivity;
  },

  // returns the next activity by index for the module
  nextActivityByIndex: function () {
    const i = this.currentActivity.get('index');
    return this.activities.find(function (activity) {
      return activity.get('index') > i;
    });
  },

  // returns the next page by key for the activity. if EF.autoNextActivity
  // is set to true it finds the next page key for the whole module, so it
  // traverses multiple activities.
  //
  getNextPageByKey: function (pageKey) {
    const next = this.currentActivity.nextPageByKey(pageKey);
    if (next) {
      return next;
    }
    if (!next && EF.options.autoNextActivity) {
      const a = this.nextActivityByIndex();
      if (a) {
        return a.pages.head();
      }
    }
  },

  // returns the previous page by key for the activity. if EF.autoNextActivity
  // is set to true it finds the previous page key for the whole module, so it
  // traverses multiple activities.
  //
  //TODO: standardize and simplify methods like this across models and collections
  getPreviousPageByKey: function (pageKey) {
    const prev = this.currentActivity.previousPageByKey(pageKey);
    if (prev) {
      return prev;
    }
    if (!prev && EF.options.autoNextActivity) {
      const a = this.activities.back(this.currentActivity);
      if (a) {
        return a.pages.last();
      }
    }
  },

  // ### getNextPage
  //
  // returns the next page and sets it to the currentPage on the activity
  getNextPage: function () {
    return this.nextPage();
  },

  // Loads a specific activity and returns the starting page. takes an
  // activityKey as an argument.
  loadActivity: function (activityKey) {
    this.currentActivity = this.activities.find(function (activity) {
      return activity.get('key') === activityKey;
    });
    return this.currentActivity;
  },

  pagesCompleteTotal: function () {
    return this.getPageProgress().pagesCompleted;
  },

  getPageProgress: function () {
    const pages = {
      pagesCompleted: 0,
      totalPages: 0,
      progress: 0,
      progressPercentage: 0,
    };
    this.activities.forEach(function (activity) {
      pages.totalPages += activity.pages.length;
      pages.pagesCompleted += activity.completedPages().length;
    });
    pages.progress = pages.pagesCompleted / pages.totalPages;
    pages.progressPercentage = Math.floor(pages.progress * 100);
    return pages;
  },
  // ### completedPageKeys
  //
  // returns an array of completed pages
  completedPageKeys: function () {
    return _.map(this.completedPages(), function (page) {
      return page.get('key');
    });
  },

  // ### percentComplete
  // returns the percent complete
  percentComplete: function () {
    return this.completedPages().length / this.currentActivity.pages.length;
  },

  // ### isPageComplete
  //
  // returns boolean for the completion status of a particular pageKey
  // and activityKey combo. takes a pageKey and an activityKey
  isPageComplete: function (pageKey, activityKey) {
    const activity = this.activities.getActivityByKey(activityKey);
    if (activity) {
      return activity.isPageComplete(pageKey) || false;
    } else {
      return false;
    }
  },
  //
  // ### isActivityComplete
  //
  // returns boolean for the completion status of a particular pageKey
  // and activityKey combo. takes a pageKey and an activityKey
  isActivityComplete: function (activityKey) {
    const activity = this.activities.getActivityByKey(activityKey);
    if (activity) {
      return activity.isComplete();
    } else {
      return false;
    }
  },

  // ### clearProgress
  //
  // Clears the progress for this module
  clearProgress: function () {
    this.activities.each(activity => {
      activity.pages.each(page => {
        page.set('completed', false);
      });
      activity.set('completed', false);
    });
    this.set('completed', false);
    this.set('page_key', undefined);
    this.set('activity_key', undefined);
    if (EF.options.local && this.progress) {
      this.progress.removeLocalActivityProgress(this.get('key'));
    }
  },

  // ### percentCompleteTotal
  // returns the percent complete for all pages in all the module's activities,
  //not just the current activity
  percentCompleteTotal: function () {
    return this.getPageProgress().progress;
  },

  // ### isComplete
  // returns true or false  depending if all the activities are completed
  isComplete: function () {
    return this.activities.every(function (activity) {
      return activity.isComplete();
    });
  },

  //return completed activities
  completedActivities: function () {
    const completedActivities = [];
    this.activities.each(function (activity) {
      if (activity.isComplete()) {
        completedActivities.push(activity.get('key'));
      }
    });
    return completedActivities;
  },

  // Set the users pathing
  setPathByKeys: function (pathKeys) {
    if (!_.isArray(pathKeys)) {
      pathKeys = new Array(pathKeys);
    }
    this.pathKeys = pathKeys;
    this.activities.each(function (activity) {
      activity.setPathByKeys(pathKeys);
    });
  },

  resetPathing: function () {
    this.activities.each(function (activity) {
      activity.resetPathing();
    });
  },

  recordGradeForSlug: function (grade, slug) {
    this.progress.saveActivityProgress(grade, null, slug);
  },

  completeAllPages: function () {
    this.reconcileSavedOptionalPages(); //attempt to reconcile saved optional pages when a module is complete
    this.saveAllOptionalPages(); // ensure all optional pages are saved
    EF.ContentProvider.ModuleService.completePages(this);
  },

  /**
   * Foundry doesn't do any extra tracking of when all the pages in an activity are complete,
   * it only cares about when the last page of the last activity is completed
   * as it uses that to set that part of the course map complete in its progress data.
   */
  saveLastSkippedModulePage: function () {
    if (this.progress && this.has('skippedLastPage')) {
      const skippedLastPage = this.get('skippedLastPage');
      this.progress.savePage(
        {
          // this will be used by _finalizeSave but do nothing
          trigger: () => {},
        },
        skippedLastPage,
        () => {}
      );
    }
  },
  saveAllOptionalPages: function () {
    this.activities.forEach(activity => {
      activity.pages.forEach(page => {
        if (page.get('optional')) {
          this.saveOptionalPage(page);
        }
      });
    });
  },
  getCompletedOptionalPages: function () {
    return EnrollmentDataService.getData('completedOptionalPages') || [];
  },

  saveOptionalPage: function (page, options = {}) {
    const completedOptionalPages = this.getCompletedOptionalPages();
    const fullPageKey = page.path();
    const { catchUp, at } = options;

    if (completedOptionalPages.includes(fullPageKey)) {
      return;
    }

    // determine whether a page can be added in-place at some positions
    const canAddInPlace = catchUp && at > -1;
    if (canAddInPlace) {
      completedOptionalPages.splice(at, 0, fullPageKey);
    }

    const pagesToSave = canAddInPlace ? completedOptionalPages : [...completedOptionalPages, fullPageKey];
    EnrollmentDataService.saveData('completedOptionalPages', pagesToSave);
  },
  reconcileSavedOptionalPages: function () {
    this.activities.forEach(activity => {
      this.newOptionalPages(activity); // check for new optional pages that we have not accounted for
      this.outdatedOptionalPages(activity); // check for new pages that are now optional
    });
  },
  newOptionalPages: function (activity) {
    const knownSavedOptionalPages = this.getCompletedOptionalPages();
    // get optional pages in this activity
    const optionalActivityPages = activity.pages.models.filter(p => p.get('optional'));
    // if none, then we don't need to do anything
    if (!optionalActivityPages.length) {
      return;
    }

    // for all the optional pages in this activity, we want to ensure that they are part of the saved pages
    optionalActivityPages.forEach(page => {
      const canReconcile = !knownSavedOptionalPages.includes(page.path());
      if (!canReconcile) {
        return;
      }
      // we found something that was not optional but is now optional.
      // therefore let's try save it, but before saving it, we need to know where to save it
      // all we really need to make sure of is that it is never the last page saved
      const index = this.getIndexToSave(knownSavedOptionalPages) - 1;
      const properIndex = index < 0 ? 0 : index;
      this.saveOptionalPage(page, { catchUp: true, at: properIndex });
    });
  },
  outdatedOptionalPages: function (activity) {
    const knownSavedOptionalPages = this.getCompletedOptionalPages();
    const nonOptionalActivityPages = activity.pages.models.filter(p => !p.get('optional'));
    if (!nonOptionalActivityPages.length) {
      return;
    }
    // we also want to ensure that no optional page because option and we didn't know about it
    nonOptionalActivityPages.forEach(page => {
      const path = page.path();
      const indexInSavedOptionals = knownSavedOptionalPages.indexOf(path);
      if (indexInSavedOptionals === -1) {
        return;
      }
      //oops a page was optional and changed to non-optional
      knownSavedOptionalPages.splice(indexInSavedOptionals, 1);
      EnrollmentDataService.saveData('completedOptionalPages', [...knownSavedOptionalPages]);
    });
  },
  getIndexToSave: function (savedOptionalPages) {
    // what matters is that we do not place the page as the last page,
    //since that can be used to direct the user back to where they left off
    const lastSavedOptionalPath = savedOptionalPages.slice(-1);
    return savedOptionalPages.indexOf(lastSavedOptionalPath);
  },
});

export default Module;
